import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import '~/components/save-button/save-button';
import '~/components/icon/icon';

@customElement('dvg-accommodation-gallery')
export class DvgGallery extends LitElement {
  @query('.images')
  private container!: HTMLUListElement;

  @query('.lightbox')
  private lightbox!: HTMLDialogElement;

  @state()
  private _selectedImage: string | null = null;

  @state()
  private _selectedIndex: number = 0;

  @state()
  private _lightboxOpen: boolean = false;

  private _touchStartX: number = 0;
  private _touchStartY: number = 0;

  @property({ type: Array })
  images: string[] = [];

  @property({ type: Boolean, attribute: 'is-saved' })
  isSaved: boolean = false;

  protected willUpdate(_changedProperties: PropertyValues) {
    super.willUpdate(_changedProperties);

    if (_changedProperties.has('images') && this.images.length > 0) {
      this._selectedImage = this.images[0];
      this._selectedIndex = 0;
    }
  }

  firstUpdated() {
    this.initDragScroll();
  }

  private initDragScroll() {
    if (!this.container) return;

    let isDown = false;
    let startX: number;
    let scrollLeft: number;
    let isDragging = false;
    let dragTimeout: number;
    let speed = 1;

    this.container.addEventListener('mousedown', (e) => {
      isDown = true;
      startX = e.pageX - this.container.offsetLeft;
      scrollLeft = this.container.scrollLeft;

      dragTimeout = window.setTimeout(() => {
        if (isDown) {
          this.container.classList.add('grabbing');
          isDragging = true;
        }
      }, 100);
    });

    this.container.addEventListener('mouseleave', () => {
      isDown = false;
      isDragging = false;
      clearTimeout(dragTimeout);
      this.container.classList.remove('grabbing');
    });

    this.container.addEventListener('mouseup', () => {
      isDown = false;
      isDragging = false;
      clearTimeout(dragTimeout);
      this.container.classList.remove('grabbing');
    });

    this.container.addEventListener('mousemove', (e) => {
      if (!isDown) return;

      const x = e.pageX - this.container.offsetLeft;
      const distance = Math.abs(x - startX);

      if (distance > 5 && !isDragging) {
        clearTimeout(dragTimeout);
        isDragging = true;
        this.container.classList.add('grabbing');
      }

      if (!isDragging) return;

      e.preventDefault();
      const walk = (x - startX) * speed;
      this.container.scrollLeft = scrollLeft - walk;
    });

    this.container.addEventListener('click', (e) => {
      if (isDragging) {
        e.preventDefault();
        e.stopPropagation();
      }
    }, true);
  }

  private move(direction: 'left' | 'right', step = 1) {
    const selectedImageIndex = this.images.findIndex((image) =>
      image === this._selectedImage
    );

    let nextImageIndex = selectedImageIndex + (direction === 'left' ? -step : step);
    if (nextImageIndex < 0) {
      nextImageIndex = this.images.length - 1;
    } else if (nextImageIndex >= this.images.length) {
      nextImageIndex = 0;
    }
    this._selectedImage = this.images[nextImageIndex];
    this._selectedIndex = nextImageIndex;

    if (this.container) {
      const nextThumb = this.container.children[nextImageIndex] as HTMLElement;
      nextThumb?.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }

  private moveLeft() {
    this.move('left');
  }

  private moveRight() {
    this.move('right');
  }

  private handleSaveClick(e: Event) {
    e.preventDefault();
    e.stopPropagation();
  }

  private openLightbox() {
    this._lightboxOpen = true;
    this.lightbox?.showModal();
    document.body.style.overflow = 'hidden';
  }

  private closeLightbox() {
    this._lightboxOpen = false;
    this.lightbox?.close();
    document.body.style.overflow = '';
  }

  private handleLightboxClick(e: Event) {
    const target = e.target as HTMLElement;
    if (target.classList.contains('lightbox') || target.classList.contains('lightbox-backdrop')) {
      this.closeLightbox();
    }
  }

  private handleLightboxTouchStart(e: TouchEvent) {
    this._touchStartX = e.touches[0].clientX;
    this._touchStartY = e.touches[0].clientY;
  }

  private handleLightboxTouchEnd(e: TouchEvent) {
    const deltaX = e.changedTouches[0].clientX - this._touchStartX;
    const deltaY = e.changedTouches[0].clientY - this._touchStartY;

    // Ignore if primarily a vertical gesture
    if (Math.abs(deltaY) > Math.abs(deltaX)) return;
    if (Math.abs(deltaX) < 50) return;

    if (deltaX < 0) {
      this.moveRight();
    } else {
      this.moveLeft();
    }
  }

  private handleLightboxKeydown(e: KeyboardEvent) {
    switch (e.key) {
      case 'Escape':
        this.closeLightbox();
        break;
      case 'ArrowLeft':
        this.moveLeft();
        break;
      case 'ArrowRight':
        this.moveRight();
        break;
    }
  }

  render() {
    if (this.images.length > 0) {
      return html`
        <div class="wrapper">
          <div class="main-image">
            <dvg-save-button
              class="save-button"
              ?saved="${this.isSaved}"
              @click="${this.handleSaveClick}"
            ></dvg-save-button>

            <button @click="${this.moveLeft}" class="arrow-btn left-arrow" aria-label="Previous image">
              <dvg-icon name="chevron-down" size="1.25rem"></dvg-icon>
            </button>
            <button @click="${this.moveRight}" class="arrow-btn right-arrow" aria-label="Next image">
              <dvg-icon name="chevron-down" size="1.25rem"></dvg-icon>
            </button>

            <span class="image-counter">${this._selectedIndex + 1}/${this.images.length}</span>

            <button class="main-image-button" @click="${this.openLightbox}" aria-label="View image fullscreen">
              <img src="${this._selectedImage}" alt="Main image" loading="eager" />
            </button>
          </div>

          <ul class="images">
            ${this.images.map((image, index) => html`
              <li class=${`thumb ${index === 0 ? 'thumb--first' : ''} ${this.isSelected(image) ? 'selected' : ''}`}>
                <button class="thumb-button" @click=${(this.updateSelectedImage(image, index))} aria-label="View image ${index + 1}">
                  <img draggable="false" class="thumb-image" src="${image}" alt="Gallery image ${index + 1}" loading="lazy" />
                </button>
              </li>
            `)}
          </ul>
        </div>

        <dialog
          class="lightbox"
          @click="${this.handleLightboxClick}"
          @keydown="${this.handleLightboxKeydown}"
          @touchstart="${this.handleLightboxTouchStart}"
          @touchend="${this.handleLightboxTouchEnd}"
          aria-label="Image gallery lightbox"
        >
          <div class="lightbox-backdrop"></div>
          <div class="lightbox-content">
            <button class="lightbox-close" @click="${this.closeLightbox}" aria-label="Close lightbox">
              <dvg-icon name="cross" size="1.5rem"></dvg-icon>
            </button>

            <button @click="${this.moveLeft}" class="lightbox-arrow lightbox-arrow--left" aria-label="Previous image">
              <dvg-icon name="chevron-down" size="1.5rem"></dvg-icon>
            </button>

            <img class="lightbox-image" src="${this._selectedImage}" alt="Gallery image ${this._selectedIndex + 1}" />

            <button @click="${this.moveRight}" class="lightbox-arrow lightbox-arrow--right" aria-label="Next image">
              <dvg-icon name="chevron-down" size="1.5rem"></dvg-icon>
            </button>

            <span class="lightbox-counter">${this._selectedIndex + 1} / ${this.images.length}</span>
          </div>
        </dialog>
      `;
    } else {
      return '';
    }
  }

  private updateSelectedImage(image: string, index: number) {
    return () => {
      this._selectedImage = image;
      this._selectedIndex = index;
    }
  }

  private isSelected(image: string) {
    return this._selectedImage === image;
  }

  static styles = css`
    :host {
      --thumbnail-height: 120px;
      --border-radius: 1.5rem;

      display: block;
    }

    .wrapper {
      display: flex;
      flex-direction: column;
      gap: 0.75rem;
    }

    .main-image {
      position: relative;
      overflow: hidden;
      border-radius: var(--border-radius);
    }

    .main-image-button {
      all: unset;
      display: block;
      width: 100%;
      cursor: zoom-in;
    }

    .save-button {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 10;
    }

    .arrow-btn {
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      z-index: 10;
      border: 0;
      cursor: pointer;
      color: white;
      background: rgba(0, 0, 0, 0.3);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 50%;
      width: 2.5rem;
      height: 2.5rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;

      &:hover,
      &:focus-visible {
        background: rgba(0, 0, 0, 0.5);
      }
    }

    .left-arrow {
      left: 0.75rem;

      dvg-icon {
        transform: rotate(90deg);
      }
    }

    .right-arrow {
      right: 0.75rem;

      dvg-icon {
        transform: rotate(-90deg);
      }
    }

    .image-counter {
      position: absolute;
      bottom: 1rem;
      right: 1rem;
      z-index: 10;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      color: white;
      padding: 0.25rem 0.75rem;
      border-radius: 1rem;
      font-size: 0.875rem;
      font-weight: 500;
    }

    .main-image img {
      width: 100%;
      height: auto;
      border-radius: var(--border-radius);
      object-fit: cover;
      aspect-ratio: 16 / 10;
    }

    .images {
      display: flex;
      gap: 0.75rem;
      overflow-x: scroll;
      scrollbar-width: none;
      scroll-snap-type: x mandatory;
      scroll-behavior: smooth;
      touch-action: pan-y;
      align-items: center;
      margin: 0;
      padding: 0;
      height: calc(var(--thumbnail-height) + 1rem);
      cursor: grab;
      user-select: none;

      &.grabbing {
        cursor: grabbing;
      }

      &.grabbing .thumb-button {
        pointer-events: none;
      }
    }

    .thumb {
      list-style: none;
      flex: 0 0 10rem;

      &.thumb--first {
        flex: 0 0 14rem;
      }

      &.selected .thumb-image {
        opacity: 1;
      }
    }

    .thumb-button {
      all: unset;
      width: 100%;
      height: var(--thumbnail-height);
      border-radius: calc(var(--border-radius) * 0.75);
      transition: all 0.3s ease-in-out;
      outline: 3px solid transparent;
      outline-offset: 2px;
      cursor: pointer;
      display: block;
      overflow: hidden;

      &:hover,
      &:focus-visible {
        transform: scale(1.03);
        outline-color: var(--dvg-orange);
      }
    }

    .thumb-image {
      width: 100%;
      height: 100%;
      border-radius: calc(var(--border-radius) * 0.75);
      object-fit: cover;
      opacity: 0.7;
      transition: opacity 0.3s ease-in-out;
      user-select: none;
    }

    /* Lightbox styles */
    .lightbox {
      position: fixed;
      inset: 0;
      width: 100vw;
      height: 100vh;
      max-width: 100vw;
      max-height: 100vh;
      border: none;
      padding: 0;
      margin: 0;
      background: transparent;
      z-index: 9999;

      &::backdrop {
        background: rgba(0, 0, 0, 0.9);
      }
    }

    .lightbox-backdrop {
      position: absolute;
      inset: 0;
      cursor: pointer;
    }

    .lightbox-content {
      position: relative;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;
      padding: 0;
      box-sizing: border-box;
      
      @media (min-width: 50rem) {
        padding: 2rem;
      }
    }

    .lightbox-close {
      position: absolute;
      top: 1rem;
      right: 1rem;
      z-index: 10;
      border: 0;
      cursor: pointer;
      color: white;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 50%;
      width: 3rem;
      height: 3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;

      &:hover,
      &:focus-visible {
        background: rgba(255, 255, 255, 0.2);
      }
    }

    .lightbox-arrow {
      position: absolute;
      top: 75%;
      transform: translateY(-50%);
      z-index: 10;
      border: 0;
      cursor: pointer;
      color: white;
      background: rgba(255, 255, 255, 0.1);
      backdrop-filter: blur(8px);
      -webkit-backdrop-filter: blur(8px);
      border-radius: 50%;
      width: 3rem;
      height: 3rem;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background-color 0.2s ease;

      &:hover,
      &:focus-visible {
        background: rgba(255, 255, 255, 0.2);
      }
      
      @media (min-width: 50rem) {
        top: 50%;
      }
    }

    .lightbox-arrow--left {
      left: 1rem;

      dvg-icon {
        transform: rotate(90deg);
      }
    }

    .lightbox-arrow--right {
      right: 1rem;

      dvg-icon {
        transform: rotate(-90deg);
      }
    }

    .lightbox-image {
      max-width: 100%;
      max-height: calc(100% - 4rem);
      object-fit: contain;
      border-radius: 0.5rem;
      
      @media (min-width: 50rem) {
        max-width: calc(100% - 8rem);
      }
    }

    .lightbox-counter {
      position: absolute;
      bottom: 1.5rem;
      left: 50%;
      transform: translateX(-50%);
      color: white;
      font-size: 1rem;
      font-weight: 500;
      background: rgba(0, 0, 0, 0.5);
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
      padding: 0.5rem 1rem;
      border-radius: 1rem;
    }
  `;
}

declare global {
  interface HTMLElementTagNameMap {
    'dvg-accommodation-gallery': DvgGallery;
  }
}
